import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Slider,
  TextField,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import { Mic, PlayArrow, Pause, Delete, DarkMode, LightMode, Replay10, Forward10, Logout as LogoutIcon, ArrowBackIosNew, Menu as MenuIcon } from '@mui/icons-material';
import { Howl } from 'howler';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import axios, { AxiosRequestConfig } from 'axios';
import { DownloadedMeditation, AudioPlayerState } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { trackMeditationStart, trackMeditationComplete } from '../services/analytics';
import { UnifiedSpeechController } from '../services/unifiedSpeech';
import UserProfile from './UserProfile';
import ReferralCodeInput from './ReferralCodeInput';
import ReferralCodeHistory from './ReferralCodeHistory';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.luminawellnessapp.com';

const generationMessages = [
  'Preparing your meditation...',
  'Turning your mood into a guided session...',
  'Choosing the voice, music, and pace...',
  'Giving everything a final listen...',
  'Almost ready. Your meditation is coming up...'
];

console.log('🔧 Meditation API Configuration:');
console.log('🔧 Base URL:', BASE_URL);

// Define custom config interface
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  retry?: number;
  retryDelay?: number;
}

// Create axios instance with custom config
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 120000, // 2 minutes timeout for initial request
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add retry logic
api.interceptors.response.use(undefined, async (err) => {
  const { config, message } = err;
  const customConfig = config as CustomAxiosRequestConfig;
  
  if (!customConfig || !customConfig.retry) {
    return Promise.reject(err);
  }
  
  // retry while Network timeout or Network Error
  if (!(message.includes('timeout') || message.includes('Network Error'))) {
    return Promise.reject(err);
  }
  
  customConfig.retry -= 1;
  
  // Create new promise to see which to retry
  const backoff = new Promise((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, customConfig.retryDelay || 1000);
  });
  
  await backoff;
  return api(customConfig);
});

interface MeditationScreenProps {
  isDarkMode: boolean;
  onThemeToggle: () => void;
}

const MeditationScreen: React.FC<MeditationScreenProps> = ({ isDarkMode, onThemeToggle }) => {
  const { currentUser, logout } = useAuth();
  const [duration, setDuration] = useState(5);
  const [loading, setLoading] = useState(false);
  const [audioPath, setAudioPath] = useState<string | null>(null);
  const [playerState, setPlayerState] = useState<AudioPlayerState>({
    isPlaying: false,
    position: 0,
    duration: 0
  });
  const [downloadedMeditations, setDownloadedMeditations] = useState<DownloadedMeditation[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [themeText, setThemeText] = useState('');
  const [sound, setSound] = useState<Howl | null>(null);
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>('female');
  const [isListening, setIsListening] = useState(false);
  const [userName, setUserName] = useState('');
  const [backgroundMusic, setBackgroundMusic] = useState<'797914' | '102078' | '521533'>('797914');
  const [placeholderOpacity, setPlaceholderOpacity] = useState(0.6);
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekPosition, setSeekPosition] = useState(0);
  const [downloadNotice, setDownloadNotice] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [downloadNoticeOpen, setDownloadNoticeOpen] = useState(false);
  const [selectedDownloadId, setSelectedDownloadId] = useState<string | null>(null);
  const [selectedDownloadTitle, setSelectedDownloadTitle] = useState<string>('');
  const [speechSupported, setSpeechSupported] = useState<boolean>(() => UnifiedSpeechController.isSupported());
  const [generationView, setGenerationView] = useState<'form' | 'session'>('form');
  const [generationMessageIndex, setGenerationMessageIndex] = useState(0);
  const [generatedAudioInSession, setGeneratedAudioInSession] = useState(false);
  const [navMenuAnchor, setNavMenuAnchor] = useState<null | HTMLElement>(null);
  const speechControllerRef = useRef<UnifiedSpeechController | null>(null);
  const latestSpeechRef = useRef('');
  const listeningBaseTextRef = useRef('');

  const updateLiveTranscript = (transcript: string) => {
    const cleaned = transcript.trim();
    if (!cleaned) return;
    setThemeText(() => {
      const base = listeningBaseTextRef.current.trim();
      return base.length > 0 ? `${base} ${cleaned}` : cleaned;
    });
  };

  const appendFinalTranscript = (transcript: string) => {
    const cleaned = transcript.trim();
    if (!cleaned) return;
    setThemeText(prev => {
      const base = prev.trim();
      if (base.endsWith(cleaned)) {
        listeningBaseTextRef.current = base;
        return prev;
      }
      const next = base.length > 0 ? `${base} ${cleaned}` : cleaned;
      listeningBaseTextRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    // Load downloaded meditations from localStorage
    try {
      const savedMeditations = localStorage.getItem('downloadedMeditations');
      if (savedMeditations) {
        const parsed = JSON.parse(savedMeditations);
        if (Array.isArray(parsed)) {
          setDownloadedMeditations(parsed);
        } else {
          console.warn('Invalid downloadedMeditations data found, clearing...');
          localStorage.removeItem('downloadedMeditations');
        }
      }
    } catch (error) {
      console.error('Error loading downloaded meditations:', error);
      localStorage.removeItem('downloadedMeditations');
    }
  }, []);

  useEffect(() => {
    // Save downloaded meditations to localStorage when they change
    try {
      localStorage.setItem('downloadedMeditations', JSON.stringify(downloadedMeditations));
    } catch (error) {
      console.error('Error saving downloaded meditations:', error);
    }
  }, [downloadedMeditations]);

  // Load user name from localStorage on component mount
  useEffect(() => {
    try {
      const savedName = localStorage.getItem('meditationUserName');
      if (savedName && typeof savedName === 'string') {
        setUserName(savedName);
      }
    } catch (error) {
      console.error('Error loading user name:', error);
      localStorage.removeItem('meditationUserName');
    }
  }, []);

  // Save user name to localStorage when it changes
  useEffect(() => {
    try {
      if (userName) {
        localStorage.setItem('meditationUserName', userName);
      }
    } catch (error) {
      console.error('Error saving user name:', error);
    }
  }, [userName]);

  // Animate placeholder text to draw attention when field is empty
  useEffect(() => {
    if (!themeText) {
      const interval = setInterval(() => {
        setPlaceholderOpacity(prev => prev === 0.6 ? 0.3 : 0.6);
      }, 2000);
      return () => clearInterval(interval);
    } else {
      setPlaceholderOpacity(0.6);
    }
  }, [themeText]);

  useEffect(() => {
    if (!loading || generationView !== 'session') return;

    const interval = setInterval(() => {
      setGenerationMessageIndex(prev => Math.min(prev + 1, generationMessages.length - 1));
    }, 6500);

    return () => clearInterval(interval);
  }, [loading, generationView]);

  const generateMeditation = async (theme?: string) => {
    if (loading) return;
    setLoading(true);
    setAudioPath(null);
    setGenerationView('session');
    setGenerationMessageIndex(0);
    setGeneratedAudioInSession(true);
    setSelectedDownloadId(null);
    setSelectedDownloadTitle('');
    setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 }));
    
    // Track meditation start
    const meditationId = `meditation_${Date.now()}`;
    trackMeditationStart(meditationId, theme || 'custom');
    
    // Clean up previous sound if it exists
    if (sound) {
      sound.unload();
    }

    try {
      console.log('🧘 Generating meditation with config:');
      console.log('🧘 Theme:', theme || themeText);
      console.log('🧘 Duration:', duration, 'minutes');
      console.log('🧘 Voice Gender:', voiceGender);
      console.log('🧘 User Name:', userName);
      console.log('🧘 Background Music:', backgroundMusic);
      console.log('🧘 API Base URL:', BASE_URL);
      
      const config: CustomAxiosRequestConfig = {
        retry: 3,
        retryDelay: 1000
      };

      const payload = {
        theme: theme || themeText,
        duration_minutes: duration,
        voice_gender: voiceGender,
        name: userName,
        background_music: backgroundMusic
      };

      let responseData: any;

      if (Capacitor.isNativePlatform()) {
        const nativeResponse = await CapacitorHttp.post({
          url: `${BASE_URL}/generate`,
          headers: {
            'Content-Type': 'application/json'
          },
          data: payload
        });

        if (nativeResponse.status !== 200) {
          throw new Error(`Generate request failed: ${nativeResponse.status}`);
        }

        responseData = nativeResponse.data;
      } else {
        const response = await api.post('/generate', payload, config);
        if (response.status !== 200) {
          throw new Error(`Generate request failed: ${response.status}`);
        }
        responseData = response.data;
      }

      if (responseData) {
        const { file } = responseData;
        const fileUrl = `${BASE_URL}/${file}`;
        console.log('🧘 Generated file:', file);
        console.log('🧘 Downloading from:', fileUrl);

        let audioBlob: Blob | null = null;
        if (Capacitor.isNativePlatform()) {
          const fileName = file.split('/').pop() || `meditation_${Date.now()}.wav`;
          const cachePath = `meditations/${fileName}`;

          const { uri } = await Filesystem.getUri({
            directory: Directory.Cache,
            path: cachePath
          });

          await FileTransfer.downloadFile({
            url: fileUrl,
            path: uri,
            readTimeout: 180000,
            connectTimeout: 180000
          });

          const audioUrl = Capacitor.convertFileSrc(uri);
          setAudioPath(audioUrl);

          const newSound = new Howl({
            src: [audioUrl],
            html5: true,
            onplay: () => setPlayerState(prev => ({ ...prev, isPlaying: true })),
            onpause: () => setPlayerState(prev => ({ ...prev, isPlaying: false })),
            onstop: () => setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 })),
            onend: () => {
              setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 }));
              // Track meditation completion
              const meditationId = `meditation_${Date.now()}`;
              trackMeditationComplete(meditationId, duration * 60, theme || 'custom');
            },
            onload: () => {
              const totalDuration = newSound.duration();
              setPlayerState(prev => ({ ...prev, duration: totalDuration }));
            },
            onseek: () => {
              const position = newSound.seek();
              setPlayerState(prev => ({ ...prev, position }));
            }
          });

          setSound(newSound);
          return;
        } else {
          const audioResponse = await api.get(fileUrl, { 
            responseType: 'blob',
            timeout: 180000 // 3 minutes timeout for audio download
          });
          audioBlob = new Blob([audioResponse.data], { type: 'audio/wav' });
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        
        setAudioPath(audioUrl);
        
        const newSound = new Howl({
          src: [audioUrl],
          html5: true,
          onplay: () => setPlayerState(prev => ({ ...prev, isPlaying: true })),
          onpause: () => setPlayerState(prev => ({ ...prev, isPlaying: false })),
          onstop: () => setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 })),
          onend: () => {
            setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 }));
            // Track meditation completion
            const meditationId = `meditation_${Date.now()}`;
            trackMeditationComplete(meditationId, duration * 60, theme || 'custom');
          },
          onload: () => {
            const duration = newSound.duration();
            setPlayerState(prev => ({ ...prev, duration }));
          },
          onseek: () => {
            const position = newSound.seek();
            setPlayerState(prev => ({ ...prev, position }));
          }
        });

        setSound(newSound);
      }
    } catch (error) {
      console.error('Error generating meditation:', error);
      setGenerationView('form');
      setGeneratedAudioInSession(false);
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          alert('The request timed out. Please try again. The server might be busy.');
        } else if (error.response) {
          alert(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          alert('Could not connect to the server. Please check your internet connection.');
        } else {
          alert('An unexpected error occurred. Please try again.');
        }
      } else {
        alert('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (!sound) return;
    
    if (playerState.isPlaying) {
      sound.pause();
    } else {
      sound.play();
    }
  };

  const handleSeek = (newPosition: number) => {
    if (!sound) return;
    sound.seek(newPosition);
  };

  const handleDownload = async () => {
    if (!audioPath) {
      console.error('No audio path available for download');
      setDownloadNotice({ message: 'No audio available to download yet.', severity: 'error' });
      setDownloadNoticeOpen(true);
      return;
    }

    try {
      console.log('Starting download for audioPath:', audioPath);

      if (Capacitor.isNativePlatform()) {
        // On native, the file is already in app cache; just save it to history.
        const newMeditation: DownloadedMeditation = {
          id: Date.now().toString(),
          title: `Meditation - ${themeText}`,
          filePath: audioPath,
          downloadDate: new Date().toISOString(),
          duration: Math.round(playerState.duration || duration * 60)
        };

        setDownloadedMeditations(prev => [...prev, newMeditation]);
        console.log('Saved meditation to local history');
        setDownloadNotice({ message: 'Saved to Downloads list.', severity: 'success' });
        setDownloadNoticeOpen(true);
        return;
      }

      // Web: trigger browser download for blob URL
      const a = document.createElement('a');
      a.href = audioPath;
      a.download = `meditation-${Date.now()}.mp3`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      const newMeditation: DownloadedMeditation = {
        id: Date.now().toString(),
        title: `Meditation - ${themeText}`,
        filePath: audioPath,
        downloadDate: new Date().toISOString(),
        duration: Math.round(playerState.duration || duration * 60)
      };

      setDownloadedMeditations(prev => [...prev, newMeditation]);
      console.log('Download completed successfully');
      setDownloadNotice({ message: 'Download started.', severity: 'success' });
      setDownloadNoticeOpen(true);
    } catch (error) {
      console.error('Error downloading meditation:', error);
      setDownloadNotice({ message: 'Failed to download meditation. Please try again.', severity: 'error' });
      setDownloadNoticeOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    setDownloadedMeditations(prev => prev.filter(m => m.id !== id));
  };

  const handlePlayDownloaded = (meditation: DownloadedMeditation) => {
    if (!meditation.filePath) return;
    setSelectedDownloadId(meditation.id);
    setSelectedDownloadTitle(meditation.title);

    if (sound && audioPath === meditation.filePath) {
      if (playerState.isPlaying) {
        sound.pause();
      } else {
        sound.play();
      }
      return;
    }

    if (sound) {
      sound.unload();
    }

    setAudioPath(meditation.filePath);
    setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0, duration: 0 }));

    const newSound = new Howl({
      src: [meditation.filePath],
      html5: true,
      onplay: () => setPlayerState(prev => ({ ...prev, isPlaying: true })),
      onpause: () => setPlayerState(prev => ({ ...prev, isPlaying: false })),
      onstop: () => setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 })),
      onend: () => setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 })),
      onload: () => {
        const totalDuration = newSound.duration();
        setPlayerState(prev => ({ ...prev, duration: totalDuration }));
      },
      onseek: () => {
        const position = newSound.seek();
        setPlayerState(prev => ({ ...prev, position }));
      }
    });

    setSound(newSound);
    newSound.play();
  };

  // Clean up sound on component unmount
  useEffect(() => {
    return () => {
      if (sound) {
        sound.unload();
      }
    };
  }, [sound]);

  // Update position periodically
  useEffect(() => {
    if (!sound || !playerState.isPlaying) return;

    const interval = setInterval(() => {
      if (isSeeking) return;
      const position = sound.seek();
      setPlayerState(prev => ({ ...prev, position }));
    }, 1000);

    return () => clearInterval(interval);
  }, [sound, playerState.isPlaying, isSeeking]);

  useEffect(() => {
    return () => {
      if (!speechControllerRef.current) return;
      void speechControllerRef.current.destroy();
      speechControllerRef.current = null;
    };
  }, []);

  const startListening = async () => {
    if (isListening) return;

    listeningBaseTextRef.current = themeText;
    latestSpeechRef.current = '';
    setIsListening(true);

    console.log(`Starting voice recording on ${Capacitor.isNativePlatform() ? 'Native' : 'Web'} device`);

    if (!speechControllerRef.current) {
      speechControllerRef.current = new UnifiedSpeechController({
        onPartial: (text: string) => {
          latestSpeechRef.current = text;
          updateLiveTranscript(text);
        },
        onFinal: (text: string) => {
          latestSpeechRef.current = text;
          appendFinalTranscript(text);
        },
        onListeningChange: (listening: boolean) => {
          setIsListening(listening);
        },
        onPermissionDenied: () => {
          alert('Microphone permission denied. Please allow microphone access and try again.');
        },
        onError: (message: string) => {
          console.warn(message);
        }
      });
    }

    try {
      await speechControllerRef.current.start();
      setSpeechSupported(true);
    } catch (error) {
      console.error('Speech recognition start error:', error);
      setIsListening(false);
      const errorMessage = error instanceof Error ? error.message : 'Speech recognition failed to start.';
      if (errorMessage.toLowerCase().includes('not supported')) {
        setSpeechSupported(false);
      }
      alert(errorMessage);
    }
  };

  const stopListening = async () => {
    if (!isListening || !speechControllerRef.current) return;

    console.log('🎤 Manually stopping voice recording');
    try {
      await speechControllerRef.current.stop();
    } catch (error) {
      console.warn('Speech recognition stop error:', error);
    } finally {
      setIsListening(false);
      const pendingTranscript = speechControllerRef.current.getPendingTranscript().trim();
      const latestTranscript = latestSpeechRef.current.trim();
      const transcriptToApply = pendingTranscript || latestTranscript;
      console.log('🎤 Processing speech after manual stop:', transcriptToApply);
      appendFinalTranscript(transcriptToApply);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setDownloadNotice({ message: 'Signed out successfully.', severity: 'success' });
      setDownloadNoticeOpen(true);
    } catch (error) {
      console.error('Logout failed:', error);
      setDownloadNotice({ message: 'Sign out failed. Please try again.', severity: 'error' });
      setDownloadNoticeOpen(true);
    }
  };

  const handleExitGenerationSession = () => {
    if (sound && playerState.isPlaying) {
      sound.pause();
    }
    setDownloadNoticeOpen(false);
    setGenerationView('form');
    setActiveTab(0);
  };

  const downloadSnackbar = downloadNotice ? (
    <Snackbar
      open={downloadNoticeOpen}
      autoHideDuration={2500}
      onClose={() => setDownloadNoticeOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setDownloadNoticeOpen(false)}
        severity={downloadNotice.severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {downloadNotice.message}
      </Alert>
    </Snackbar>
  ) : undefined;

  const navigationItems = [
    { label: 'Generate', value: 0 },
    ...(currentUser ? [
      { label: 'Referral Codes', value: 3 },
      { label: 'Profile', value: 2 }
    ] : [])
  ];
  const activeSectionLabel = navigationItems.find(item => item.value === activeTab)?.label || 'Generate';
  const isNavMenuOpen = Boolean(navMenuAnchor);

  const handleNavigationSelect = (value: number) => {
    setActiveTab(value);
    setNavMenuAnchor(null);
  };

  if (generationView === 'session') {
    const sessionTitle = themeText.trim() || `Your Meditation Is Ready${userName.trim() ? `, ${userName.trim()}!` : ''}`;

    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: loading || !audioPath
            ? 'radial-gradient(circle at 13% 14%, rgba(200, 245, 238, 0.74), transparent 25%), radial-gradient(circle at 82% 13%, rgba(225, 190, 231, 0.64), transparent 27%), radial-gradient(circle at 78% 72%, rgba(255, 224, 178, 0.58), transparent 31%), radial-gradient(circle at 24% 78%, rgba(220, 237, 200, 0.62), transparent 30%), radial-gradient(circle at 50% 42%, rgba(227, 242, 253, 0.9), transparent 32%), linear-gradient(180deg, #fffefe 0%, #f9fdff 45%, #fff9fb 100%)'
            : isDarkMode
            ? 'radial-gradient(circle at 50% 38%, rgba(100, 181, 246, 0.24), transparent 34%), linear-gradient(135deg, #1a237e 0%, #311b92 50%, #4a148c 100%)'
            : 'radial-gradient(circle at 50% 35%, rgba(243, 229, 245, 0.98), transparent 31%), linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 50%, #fce4ec 100%)',
          color: isDarkMode ? 'white' : 'text.primary',
          px: { xs: 2.5, sm: 4 },
          py: { xs: 3, sm: 4 },
          position: 'relative',
          overflow: 'hidden',
          '@keyframes luminaBubbleDrift': {
            '0%, 100%': {
              transform: 'translate3d(0, 0, 0) scale(1)',
              opacity: 0.28
            },
            '50%': {
              transform: 'translate3d(10px, -18px, 0) scale(1.08)',
              opacity: 0.54
            }
          }
        }}
      >
        {[
          { size: 86, top: '12%', left: '12%', delay: '0s', duration: '7s' },
          { size: 42, top: '22%', right: '18%', delay: '1.4s', duration: '6.5s' },
          { size: 118, bottom: '12%', left: '9%', delay: '0.8s', duration: '8.5s' },
          { size: 58, bottom: '20%', right: '12%', delay: '2.1s', duration: '7.5s' },
          { size: 34, top: '48%', left: '20%', delay: '2.8s', duration: '6.8s' },
          { size: 74, top: '58%', right: '22%', delay: '3.2s', duration: '9s' }
        ].map((bubble, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              width: bubble.size,
              height: bubble.size,
              top: bubble.top,
              right: bubble.right,
              bottom: bubble.bottom,
              left: bubble.left,
              zIndex: 1,
              pointerEvents: 'none',
              borderRadius: '50%',
              border: isDarkMode
                ? '1px solid rgba(255,255,255,0.14)'
                : '1px solid rgba(103,58,183,0.13)',
              background: isDarkMode
                ? 'radial-gradient(circle at 34% 28%, rgba(255,255,255,0.18), rgba(255,255,255,0.045) 42%, transparent 72%)'
                : 'radial-gradient(circle at 34% 28%, rgba(255,255,255,0.78), rgba(255,255,255,0.28) 42%, rgba(156,39,176,0.035) 72%)',
              boxShadow: isDarkMode
                ? 'inset 0 0 18px rgba(255,255,255,0.06), 0 12px 44px rgba(0,0,0,0.08)'
                : 'inset 0 0 18px rgba(255,255,255,0.82), 0 12px 44px rgba(103,58,183,0.08)',
              animation: `luminaBubbleDrift ${bubble.duration} ease-in-out ${bubble.delay} infinite`
            }}
          />
        ))}
        {!loading && audioPath ? (
          <IconButton
            aria-label="Back to generator"
            onClick={handleExitGenerationSession}
            onTouchEnd={(event) => {
              event.preventDefault();
              handleExitGenerationSession();
            }}
            sx={{
              position: 'fixed',
              top: {
                xs: 'calc(env(safe-area-inset-top, 0px) + 14px)',
                sm: 32
              },
              left: {
                xs: 'calc(env(safe-area-inset-left, 0px) + 14px)',
                sm: 32
              },
              zIndex: 3000,
              width: { xs: 60, sm: 54 },
              height: { xs: 60, sm: 54 },
              minWidth: { xs: 60, sm: 54 },
              minHeight: { xs: 60, sm: 54 },
              borderRadius: '50%',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.16)' : '1px solid rgba(0,0,0,0.08)',
              background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.72)',
              color: isDarkMode ? 'white' : 'text.primary',
              backdropFilter: 'blur(12px)',
              pointerEvents: 'auto',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                background: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.95)'
              }
            }}
          >
            <ArrowBackIosNew fontSize="small" />
          </IconButton>
        ) : undefined}

        {loading || !audioPath ? (
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              minHeight: 'calc(100vh - 48px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              textAlign: 'center',
              maxWidth: 620,
              mx: 'auto',
              '&::before': {
                content: '""',
                position: 'fixed',
                inset: 0,
                zIndex: 0,
                pointerEvents: 'none',
                background: 'radial-gradient(circle at 12% 0%, rgba(255, 224, 178, 0.55), transparent 21%), radial-gradient(circle at 91% 0%, rgba(248, 187, 208, 0.58), transparent 24%), radial-gradient(circle at 15% 20%, rgba(178, 235, 242, 0.78), transparent 30%), radial-gradient(circle at 83% 18%, rgba(225, 190, 231, 0.72), transparent 31%), radial-gradient(circle at 82% 74%, rgba(255, 204, 188, 0.55), transparent 32%), radial-gradient(circle at 20% 78%, rgba(220, 237, 200, 0.68), transparent 31%), radial-gradient(circle at 50% 52%, rgba(255, 249, 196, 0.36), transparent 28%), linear-gradient(180deg, #ffffff 0%, #f8fdff 52%, #fff8fc 100%)',
                animation: 'luminaBreathe 7s ease-in-out infinite alternate',
                '@keyframes luminaBreathe': {
                  from: {
                    transform: 'scale(1)',
                    opacity: 0.86
                  },
                  to: {
                    transform: 'scale(1.06)',
                    opacity: 1
                  }
                }
              },
              '&::after': {
                content: '""',
                position: 'fixed',
                inset: 0,
                zIndex: 1,
                pointerEvents: 'none',
                opacity: 0.34,
                backgroundImage: 'radial-gradient(circle at 25% 30%, rgba(38, 50, 56, 0.14) 0 0.7px, transparent 0.8px), radial-gradient(circle at 72% 18%, rgba(156, 39, 176, 0.1) 0 0.6px, transparent 0.8px), radial-gradient(circle at 48% 82%, rgba(3, 169, 244, 0.1) 0 0.6px, transparent 0.8px), linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.72) 42%, transparent 72%)',
                backgroundSize: '18px 18px, 23px 23px, 27px 27px, 100% 100%',
                animation: 'luminaDrift 11s ease-in-out infinite alternate',
                '@keyframes luminaDrift': {
                  from: { transform: 'translateX(-7%)' },
                  to: { transform: 'translateX(7%)' }
                }
              },
              '& > *': {
                position: 'relative',
                zIndex: 3
              }
            }}
          >
            <Box
              sx={{
                position: 'fixed',
                top: { xs: '13%', sm: '16%' },
                left: { xs: '50%', sm: '50%' },
                width: { xs: 260, sm: 360 },
                height: { xs: 260, sm: 360 },
                transform: 'translateX(-50%)',
                zIndex: 2,
                pointerEvents: 'none',
                borderRadius: '50%',
                border: '1px solid rgba(156,39,176,0.07)',
                boxShadow: '0 0 0 34px rgba(129,212,250,0.035), 0 0 0 72px rgba(248,187,208,0.035)',
                opacity: 0.9
              }}
            />
            <Box
              sx={{
                position: 'fixed',
                right: { xs: -70, sm: -20 },
                bottom: { xs: '18%', sm: '16%' },
                width: { xs: 180, sm: 240 },
                height: { xs: 180, sm: 240 },
                zIndex: 2,
                pointerEvents: 'none',
                borderRadius: '50%',
                border: '1px solid rgba(3,169,244,0.08)',
                boxShadow: '0 0 0 28px rgba(220,237,200,0.04)',
                opacity: 0.8
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
                fontSize: { xs: '2.05rem', sm: '2.75rem' },
                lineHeight: 1.16,
                fontWeight: 650,
                letterSpacing: 0,
                maxWidth: 600,
                color: isDarkMode ? 'rgba(255,255,255,0.94)' : '#252334',
                textShadow: isDarkMode
                  ? '0 10px 30px rgba(0,0,0,0.28)'
                  : '0 10px 30px rgba(103,58,183,0.08)'
              }}
            >
              {generationMessages[generationMessageIndex]}
            </Typography>
            <Box
              aria-label="Preparing meditation"
              sx={{
                width: { xs: 168, sm: 190 },
                height: { xs: 168, sm: 190 },
                position: 'relative',
                borderRadius: '50%',
                '@keyframes luminaPulse': {
                  '0%': { transform: 'scale(0.72)', opacity: 0.18 },
                  '45%': { opacity: 0.72 },
                  '100%': { transform: 'scale(1.2)', opacity: 0 }
                },
                '@keyframes luminaFloat': {
                  '0%, 100%': { transform: 'translate3d(-3px, 4px, 0) scale(1)' },
                  '50%': { transform: 'translate3d(5px, -6px, 0) scale(1.04)' }
                }
              }}
            >
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: isDarkMode
                      ? '1px solid rgba(255,255,255,0.16)'
                      : '1px solid rgba(103,58,183,0.16)',
                    background: index === 0
                      ? 'radial-gradient(circle, rgba(255,255,255,0.52) 0%, rgba(129,212,250,0.28) 34%, rgba(186,104,200,0.14) 58%, transparent 72%)'
                      : index === 1
                      ? 'radial-gradient(circle, rgba(244,143,177,0.28) 0%, rgba(179,157,219,0.2) 42%, transparent 72%)'
                      : 'radial-gradient(circle, rgba(178,235,242,0.26) 0%, rgba(225,190,231,0.18) 48%, transparent 74%)',
                    filter: 'blur(0.2px)',
                    animation: `luminaPulse ${3.9 + index * 0.8}s ease-out ${index * 0.55}s infinite`
                  }}
                />
              ))}
              <Box
                sx={{
                  position: 'absolute',
                  inset: { xs: 44, sm: 50 },
                  borderRadius: '44% 56% 52% 48%',
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(129,212,250,0.78), rgba(186,104,200,0.62), rgba(244,143,177,0.42))'
                    : 'linear-gradient(135deg, rgba(129,212,250,0.8), rgba(225,190,231,0.72), rgba(248,187,208,0.62))',
                  boxShadow: isDarkMode
                    ? '0 24px 70px rgba(0,0,0,0.26), inset 0 0 26px rgba(255,255,255,0.16)'
                    : '0 24px 70px rgba(156,39,176,0.16), inset 0 0 26px rgba(255,255,255,0.72)',
                  animation: 'luminaFloat 4.8s ease-in-out infinite'
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              minHeight: 'calc(100vh - 48px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              maxWidth: 760,
              mx: 'auto',
              pt: { xs: 8, sm: 6 }
            }}
          >
            <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.75rem' },
                  lineHeight: 1.15,
                  fontWeight: 700
                }}
              >
                {sessionTitle}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                height: { xs: 320, sm: 390 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: { xs: 3, sm: 4 }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: { xs: 310, sm: 430 },
                  height: { xs: 220, sm: 290 },
                  borderRadius: '50%',
                  filter: 'blur(30px)',
                  background: isDarkMode
                    ? 'radial-gradient(circle, rgba(100,181,246,0.76) 0%, rgba(156,39,176,0.56) 46%, rgba(63,81,181,0.24) 72%, transparent 100%)'
                    : 'radial-gradient(circle, rgba(156,39,176,0.48) 0%, rgba(103,58,183,0.34) 43%, rgba(100,181,246,0.28) 67%, transparent 100%)',
                  opacity: 0.9
                }}
              />
              <IconButton
                aria-label="Rewind 10 seconds"
                onClick={() => handleSeek(Math.max(0, playerState.position - 10))}
                sx={{
                  width: { xs: 66, sm: 78 },
                  height: { xs: 66, sm: 78 },
                  mr: { xs: 2, sm: 4 },
                  border: '1px solid rgba(255,255,255,0.45)',
                  background: 'rgba(255,255,255,0.24)',
                  color: 'white',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Replay10 />
              </IconButton>
              <IconButton
                aria-label={playerState.isPlaying ? 'Pause meditation' : 'Play meditation'}
                onClick={handlePlayPause}
                sx={{
                  width: { xs: 116, sm: 144 },
                  height: { xs: 116, sm: 144 },
                  background: isDarkMode
                    ? 'linear-gradient(135deg, #64b5f6, #42a5f5)'
                    : 'linear-gradient(135deg, #9c27b0, #673ab7)',
                  color: 'white',
                  boxShadow: isDarkMode
                    ? '0 18px 50px rgba(0,0,0,0.28)'
                    : '0 18px 50px rgba(156,39,176,0.25)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'linear-gradient(135deg, #42a5f5, #2196f3)'
                      : 'linear-gradient(135deg, #673ab7, #5e35b1)'
                  }
                }}
              >
                {playerState.isPlaying ? <Pause sx={{ fontSize: 42 }} /> : <PlayArrow sx={{ fontSize: 48 }} />}
              </IconButton>
              <IconButton
                aria-label="Forward 10 seconds"
                onClick={() => handleSeek(Math.min(playerState.duration, playerState.position + 10))}
                sx={{
                  width: { xs: 66, sm: 78 },
                  height: { xs: 66, sm: 78 },
                  ml: { xs: 2, sm: 4 },
                  border: '1px solid rgba(255,255,255,0.45)',
                  background: 'rgba(255,255,255,0.24)',
                  color: 'white',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Forward10 />
              </IconButton>
            </Box>

            <Box sx={{ px: { xs: 0, sm: 2 } }}>
              <Slider
                value={isSeeking ? seekPosition : playerState.position}
                max={playerState.duration}
                onChange={(_, value) => {
                  setIsSeeking(true);
                  setSeekPosition(value as number);
                }}
                onChangeCommitted={(_, value) => {
                  setIsSeeking(false);
                  handleSeek(value as number);
                }}
                sx={{
                  color: isDarkMode ? '#64b5f6' : '#9c27b0',
                  '& .MuiSlider-rail': {
                    opacity: 0.22
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
                <Typography variant="body2" color="text.secondary">{formatTime(playerState.position)}</Typography>
                <Typography variant="body2" color="text.secondary">{formatTime(playerState.duration)}</Typography>
              </Box>
            </Box>

          </Box>
        )}
        {downloadSnackbar}
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      {/* Enhanced Header with Gradient */}
      <Box 
        sx={{ 
          my: 4,
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1a237e 0%, #311b92 50%, #4a148c 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 50%, #fce4ec 100%)',
          borderRadius: 3,
          p: 3,
          boxShadow: isDarkMode 
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: isDarkMode 
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(156, 39, 176, 0.1)',
            opacity: 0.6
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: isDarkMode 
              ? 'rgba(255, 255, 255, 0.08)'
              : 'rgba(33, 150, 243, 0.1)',
            opacity: 0.4
          }}
        />
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          position: 'relative',
          zIndex: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Meditation-themed icon */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: isDarkMode 
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(156, 39, 176, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(156, 39, 176, 0.3)'}`
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  color: isDarkMode ? 'white' : 'primary.main',
                  fontWeight: 300,
                  fontSize: '1.2rem'
                }}
              >
                🧘
              </Typography>
            </Box>
            
          <Box>
                            <Typography 
                variant="h4" 
                component="h1"
                sx={{
                  fontSize: {
                    xs: '1.5rem',
                    sm: '2.125rem',
                    md: '2.125rem'
                  },
                  lineHeight: {
                    xs: 1.2,
                    sm: 1.235,
                    md: 1.235
                  },
                  color: isDarkMode ? 'white' : 'text.primary',
                  fontWeight: 600,
                  textShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : 'none'
                }}
              >
                Lumina,<br />
                Your Personal Meditation Pal
              </Typography>
            {currentUser && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 0.5,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
                    fontWeight: 500
                  }}
                >
                Welcome back, {currentUser.displayName || currentUser.email}
              </Typography>
            )}
          </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={onThemeToggle}
              sx={{
                background: isDarkMode 
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(156, 39, 176, 0.1)',
                color: isDarkMode ? 'white' : 'primary.main',
                '&:hover': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(156, 39, 176, 0.2)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {isDarkMode ? <LightMode /> : <DarkMode />}
            </IconButton>
            <IconButton 
              onClick={handleLogout}
              sx={{ 
                background: isDarkMode 
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(244, 67, 54, 0.1)',
                color: isDarkMode ? 'white' : 'error.main',
                '&:hover': { 
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(244, 67, 54, 0.2)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Box>
          </Box>
        </Box>

      {/* Enhanced Card-Based Layout */}
      <Paper 
        elevation={isDarkMode ? 8 : 4}
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: isDarkMode 
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.08)',
          mb: 3
        }}
      >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              background: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(156, 39, 176, 0.05)',
              borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '1.05rem', sm: '1.2rem' },
                fontWeight: 700,
                color: isDarkMode ? 'white' : 'primary.main'
              }}
            >
              {activeSectionLabel}
            </Typography>
            <IconButton
              aria-label="Open navigation menu"
              aria-controls={isNavMenuOpen ? 'section-navigation-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isNavMenuOpen ? 'true' : undefined}
              onClick={(event) => setNavMenuAnchor(event.currentTarget)}
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                background: isDarkMode
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(156, 39, 176, 0.1)',
                color: isDarkMode ? 'white' : 'primary.main',
                '&:hover': {
                  background: isDarkMode
                    ? 'rgba(255, 255, 255, 0.18)'
                    : 'rgba(156, 39, 176, 0.18)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="section-navigation-menu"
              anchorEl={navMenuAnchor}
              open={isNavMenuOpen}
              onClose={() => setNavMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 210,
                  borderRadius: 2,
                  border: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 16px 36px rgba(0,0,0,0.18)'
                }
              }}
            >
              {navigationItems.map((item) => (
                <MenuItem
                  key={item.value}
                  selected={activeTab === item.value}
                  onClick={() => handleNavigationSelect(item.value)}
                  sx={{
                    py: 1.25,
                    fontWeight: activeTab === item.value ? 700 : 500,
                    color: activeTab === item.value ? 'primary.main' : 'text.primary'
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {activeTab === 0 && (
            <Box sx={{ mt: 2 }}>
              {/* Enhanced Form Fields */}
              <TextField
                fullWidth
                label="Your Name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name or nick name for personalized meditation"
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: 'text.secondary' }}>
                      👤
                    </Box>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Meditation Theme or Tell Me Your Mood"
                value={themeText}
                onChange={(e) => setThemeText(e.target.value)}
                multiline
                rows={1}
                placeholder={themeText ? '' : 'Type your theme/mood or use voice recording below...'}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: placeholderOpacity,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                    transition: 'opacity 1s ease-in-out'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }}>
                      🧘‍♀️
                    </Box>
                  )
                }}
              />
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                backgroundColor: isListening ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
                padding: '8px',
                borderRadius: '4px',
                transition: 'background-color 0.3s ease'
              }}>
                <IconButton 
                  onClick={isListening ? stopListening : startListening}
                  disabled={!speechSupported}
                  sx={{
                    background: isListening 
                      ? 'rgba(244, 67, 54, 0.1)' 
                      : 'rgba(33, 150, 243, 0.1)',
                    color: isListening ? 'error.main' : 'primary.main',
                    '&:hover': {
                      background: isListening 
                        ? 'rgba(244, 67, 54, 0.2)' 
                        : 'rgba(33, 150, 243, 0.2)',
                      transform: 'scale(1.05)'
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.1)',
                      color: 'text.disabled'
                    },
                    transition: 'all 0.2s ease',
                    width: 48,
                    height: 48
                  }}
                >
                  <Mic color={isListening ? 'error' : 'primary'} />
                </IconButton>
                <Typography sx={{ ml: 1, flex: 1 }}>
                  {!speechSupported
                    ? 'Speech recognition not supported in this browser'
                    : isListening 
                    ? 'Listening... Tap mic to stop'
                    : 'Tap mic to speak'}
                </Typography>
              </Box>

              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '2fr 3fr',
                gap: 4,
                '& > :last-child': {
                  paddingLeft: 0
                }
              }}>
                <FormControl component="fieldset">
                  <Typography gutterBottom>Voice Gender</Typography>
                  <RadioGroup
                    value={voiceGender}
                    onChange={(e) => setVoiceGender(e.target.value as 'male' | 'female')}
                  >
                    <FormControlLabel value="female" control={<Radio />} label="Female" />
                    <FormControlLabel value="male" control={<Radio />} label="Male" />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset">
                  <Typography gutterBottom>Background Music</Typography>
                  <RadioGroup
                    value={backgroundMusic}
                    onChange={(e) => setBackgroundMusic(e.target.value as '797914' | '102078' | '521533')}
                  >
                    <FormControlLabel value="797914" control={<Radio />} label="Relaxation Music" /> 
                    <FormControlLabel value="102078" control={<Radio />} label="Water Current" />
                    <FormControlLabel value="521533" control={<Radio />} label="Forest" />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Typography gutterBottom>
                Duration: {duration} minutes
              </Typography>
              <Slider
                value={duration}
                onChange={(_, value) => setDuration(value as number)}
                min={1}
                max={10}
                step={1}
                sx={{ mb: 3 }}
              />

              {/* Enhanced Generate Button */}
              <Button
                variant="contained"
                onClick={() => generateMeditation()}
                disabled={loading}
                fullWidth
                sx={{
                  py: 2,
                  px: 3,
                  borderRadius: 3,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, #64b5f6, #42a5f5)'
                    : 'linear-gradient(135deg, #9c27b0, #673ab7)',
                  boxShadow: isDarkMode 
                    ? '0 8px 32px rgba(100, 181, 246, 0.3)'
                    : '0 8px 32px rgba(156, 39, 176, 0.3)',
                  '&:hover': {
                    background: isDarkMode 
                      ? 'linear-gradient(135deg, #42a5f5, #2196f3)'
                      : 'linear-gradient(135deg, #673ab7, #5e35b1)',
                    transform: 'translateY(-2px)',
                    boxShadow: isDarkMode 
                      ? '0 12px 40px rgba(100, 181, 246, 0.4)'
                      : '0 12px 40px rgba(156, 39, 176, 0.4)'
                  },
                  '&:disabled': {
                    background: isDarkMode 
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(156, 39, 176, 0.3)',
                    transform: 'none',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <span>Take a few deep breaths while we prepare your meditation — it may take up to a minute.</span>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>🧘‍♀️ Generate Meditation</span>
                  </Box>
                )}
              </Button>

              {audioPath && !generatedAudioInSession && (
                <Box sx={{ mt: 4 }}>
                  {/* Enhanced Audio Player Card */}
                  <Paper 
                    elevation={isDarkMode ? 4 : 2}
                    sx={{ 
                      p: 3,
                      borderRadius: 3,
                      background: isDarkMode 
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(156, 39, 176, 0.05)',
                      border: isDarkMode 
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(156, 39, 176, 0.1)'
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2, 
                        textAlign: 'center',
                        color: isDarkMode ? 'white' : 'primary.main',
                        fontWeight: 600
                      }}
                    >
                      🎧 Your Meditation
                    </Typography>
                    
                    {/* Enhanced Audio Controls */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mb: 3, 
                      gap: { xs: 1, sm: 2 },
                      flexWrap: 'wrap'
                    }}>
                      <IconButton 
                        onClick={() => handleSeek(Math.max(0, playerState.position - 10))}
                        sx={{
                          background: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(156, 39, 176, 0.1)',
                          color: isDarkMode ? 'white' : 'primary.main',
                          '&:hover': {
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.2)'
                              : 'rgba(156, 39, 176, 0.2)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease',
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 }
                        }}
                      >
                      <Replay10 />
                    </IconButton>
                      
                      <IconButton 
                        onClick={handlePlayPause}
                        sx={{
                          background: isDarkMode 
                            ? 'linear-gradient(135deg, #64b5f6, #42a5f5)'
                            : 'linear-gradient(135deg, #9c27b0, #673ab7)',
                          color: 'white',
                          '&:hover': {
                            background: isDarkMode 
                              ? 'linear-gradient(135deg, #42a5f5, #2196f3)'
                              : 'linear-gradient(135deg, #673ab7, #5e35b1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease',
                          width: { xs: 64, sm: 72 },
                          height: { xs: 64, sm: 72 },
                          boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                        }}
                      >
                      {playerState.isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>
                      
                      <IconButton 
                        onClick={() => handleSeek(Math.min(playerState.duration, playerState.position + 10))}
                        sx={{
                          background: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(156, 39, 176, 0.1)',
                          color: isDarkMode ? 'white' : 'primary.main',
                          '&:hover': {
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.2)'
                              : 'rgba(156, 39, 176, 0.2)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease',
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 }
                        }}
                      >
                      <Forward10 />
                    </IconButton>
                  </Box>
                    
                    {/* Enhanced Progress Bar */}
                    <Box sx={{ mb: 3 }}>
                  <Slider
                    value={isSeeking ? seekPosition : playerState.position}
                    max={playerState.duration}
                    onChange={(_, value) => {
                      setIsSeeking(true);
                      setSeekPosition(value as number);
                    }}
                    onChangeCommitted={(_, value) => {
                      setIsSeeking(false);
                      handleSeek(value as number);
                    }}
                        sx={{
                          '& .MuiSlider-track': {
                            background: isDarkMode 
                              ? 'linear-gradient(90deg, #64b5f6, #42a5f5)'
                              : 'linear-gradient(90deg, #9c27b0, #673ab7)',
                            height: 6,
                            borderRadius: 3
                          },
                          '& .MuiSlider-rail': {
                            height: 6,
                            borderRadius: 3,
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.2)'
                              : 'rgba(156, 39, 176, 0.2)'
                          },
                          '& .MuiSlider-thumb': {
                            width: 20,
                            height: 20,
                            background: isDarkMode 
                              ? 'linear-gradient(135deg, #64b5f6, #42a5f5)'
                              : 'linear-gradient(135deg, #9c27b0, #673ab7)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            '&:hover': {
                              transform: 'scale(1.2)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                            }
                          }
                        }}
                      />
                      
                      {/* Time Display */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mt: 1
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                      {formatTime(playerState.position)}
                    </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                      {formatTime(playerState.duration)}
                    </Typography>
                  </Box>
                    </Box>
                    
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ mt: 3 }}>
              {downloadedMeditations.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary'
                }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    📥 No Downloads Yet
                  </Typography>
                  <Typography variant="body2">
                    Generate your first meditation to see it here
                  </Typography>
                </Box>
              ) : (
                <List sx={{ 
                  mt: 3,
                  '& .MuiListItem-root': {
                    borderRadius: 2,
                    mb: 1,
                    background: isDarkMode 
                      ? 'rgba(255, 255, 255, 0.03)'
                      : 'rgba(0, 0, 0, 0.02)',
                    border: isDarkMode 
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : '1px solid rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      background: isDarkMode 
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.05)',
                      transform: 'translateX(4px)'
                    },
                    transition: 'all 0.2s ease'
                  }
                }}>
              {[...downloadedMeditations]
                .sort((a, b) => new Date(b.downloadDate).getTime() - new Date(a.downloadDate).getTime())
                .map((meditation) => (
                <ListItem key={meditation.id}>
                  <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {meditation.title}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              📅 {new Date(meditation.downloadDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              ⏱️ {Math.floor(meditation.duration / 60)} min
                            </Typography>
                          </Box>
                        }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handlePlayDownloaded(meditation)}
                      sx={{
                        color: 'primary.main',
                        mr: 1,
                        '&:hover': {
                          background: 'rgba(156, 39, 176, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <PlayArrow />
                    </IconButton>
                    <IconButton 
                      edge="end" 
                      onClick={() => handleDelete(meditation.id)}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          background: 'rgba(244, 67, 54, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
                </List>
              )}

              {selectedDownloadId && audioPath && (
                <Paper
                  elevation={isDarkMode ? 4 : 2}
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Now playing: {selectedDownloadTitle}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={handlePlayPause} sx={{ color: 'primary.main' }}>
                      {playerState.isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>
                    <Slider
                      value={isSeeking ? seekPosition : playerState.position}
                      max={playerState.duration}
                      onChange={(_, value) => {
                        setIsSeeking(true);
                        setSeekPosition(value as number);
                      }}
                      onChangeCommitted={(_, value) => {
                        setIsSeeking(false);
                        handleSeek(value as number);
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Typography variant="caption" sx={{ minWidth: 60, textAlign: 'right' }}>
                      {formatTime(playerState.position)} / {formatTime(playerState.duration)}
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {activeTab === 2 && currentUser && (
            <UserProfile />
          )}

          {activeTab === 3 && currentUser && (
            <Box>
              <Paper 
                elevation={isDarkMode ? 4 : 2}
                sx={{ 
                  p: 3, 
                  mb: 3,
                  borderRadius: 3,
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(156, 39, 176, 0.03)',
                  border: isDarkMode 
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(156, 39, 176, 0.1)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: isDarkMode 
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(156, 39, 176, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography sx={{ fontSize: '1.2rem' }}>🎁</Typography>
                  </Box>
                  <Typography variant="h6" component="h3">
                  Apply Referral Code
                </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Enter a referral code to track which affiliate partner brought you to our meditation app.
                </Typography>
                <ReferralCodeInput 
                  variant="outlined"
                  size="medium"
                  placeholder="Enter referral code"
                  onSuccess={(validation) => {
                    console.log('Referral code applied:', validation);
                    // You can add additional logic here like showing a success message
                  }}
                />
              </Paper>
              
              <ReferralCodeHistory />
            </Box>
          )}
        </Box>
        </Paper>
      {downloadSnackbar}
    </Container>
  );
};

// Helper function to format time
const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default MeditationScreen; 
